"use client";

import type { ElementType, HTMLAttributes, FC, Ref } from "react";
import { motion } from "motion/react";
import { mergeClasses } from "@huub-web/utils";
import type { RecipeVariants } from "@vanilla-extract/recipes";

import type { ApplyMotionProp, RequiredChildrenProp } from "../../props";
import { cardCollectionMotionVariants } from "../../config/cardCollectionMotion";
import { Text } from "../Text";
import { Flex } from "../Flex";

import { gridContainerStyle, gridStyle, gridTitleStyle } from "./Grid.css";

export type GridProps = HTMLAttributes<HTMLDivElement> &
  RequiredChildrenProp &
  RecipeVariants<typeof gridContainerStyle> &
  ApplyMotionProp & {
    as?: ElementType;
    /**
     * @deprecated use Heading component instead
     */
    title?: string;
    /**
     * @deprecated use Heading component instead
     */
    description?: string;
    /**
     * Ref forwarded to the root element
     */
    ref?: Ref<HTMLDivElement>;
  };

/**
 * A grid component that can be used to layout components.
 */
export const Grid: FC<GridProps> = ({
  variant,
  title,
  description,
  applyMotion = false,
  gap,
  className,
  children,
  ref,
  ...props
}) => (
  <>
    {/* @deprecated use Heading component instead */}
    {(title || description) && (
      <Flex direction="column" gap="xs">
        <Text as="h3" variant="displayS" className={gridTitleStyle} bold>
          {title}
        </Text>
        {description && (
          <Text as="p" variant="bodyS" color="currentColor50">
            {description}
          </Text>
        )}
      </Flex>
    )}

    <motion.div
      ref={ref}
      className={mergeClasses(
        gridStyle,
        gridContainerStyle({ variant, gap }),
        className,
      )}
      variants={applyMotion ? cardCollectionMotionVariants : undefined}
      initial={applyMotion ? "hidden" : undefined}
      animate={applyMotion ? "visible" : undefined}
      exit={applyMotion ? "exit" : undefined}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      {...(props as any)}
    >
      {children}
    </motion.div>
  </>
);
